/** Job template editor: header, tasks (sortable) with checklists, parts, applicable types. */
import { useNavigate, useParams } from "@tanstack/react-router";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type JobPartIn,
  type JobTaskIn,
  type JobTemplate,
  type JobTemplateIn,
  type JobTemplatePatch,
  TEMPLATE_CATEGORIES,
  useJobTemplate,
  useSaveJobTemplate,
} from "@/features/admin/maintenance/queries";
import { useAssetTypesAdmin } from "@/features/admin/reference/queries";
import { Attachments } from "@/features/documents/Attachments";
import { useEntityDocuments } from "@/features/documents/queries";
import { ChecklistEditor, type ChecklistRow } from "@/shared/components/checklist-editor";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { ItemPicker } from "@/shared/components/pickers";
import { localId, SortableList } from "@/shared/components/sortable-list";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/primitives";

interface HeaderValues extends Record<string, unknown> {
  code: string;
  name: string;
  name_ar: string;
  category: string;
  description: string;
  instructions: string;
  sop_document_id: string;
  est_duration_minutes: string;
  est_labour_minutes: string;
  requires_verification: boolean;
  requires_meter_reading: boolean;
  skills: string;
  is_active: boolean;
}

/** Client-side task row (with ids for sorting). */
export interface TaskRow extends JobTaskIn {
  _id: string;
  checklist: ChecklistRow[];
}

/** Client-side checklist row. */
export type { ChecklistRow } from "@/shared/components/checklist-editor";

/** Client-side part row. */
export interface PartRow extends JobPartIn {
  _id: string;
}

/** Convert API template rows into editable rows. */
export function toRows(template: JobTemplate | undefined): { tasks: TaskRow[]; parts: PartRow[] } {
  return {
    tasks:
      template?.tasks.map((t) => ({
        _id: t.id,
        title: t.title,
        instructions: t.instructions,
        est_minutes: t.est_minutes,
        is_mandatory: t.is_mandatory,
        checklist: t.checklist.map((c) => ({
          _id: c.id,
          prompt: c.prompt,
          response_type: c.response_type,
          constraints: c.constraints,
          is_mandatory: c.is_mandatory,
          photo_required: c.photo_required,
          _options: Array.isArray((c.constraints as { options?: unknown[] }).options)
            ? ((c.constraints as { options: unknown[] }).options as string[]).join(", ")
            : "",
        })),
      })) ?? [],
    parts:
      template?.parts.map((p) => ({
        _id: p.id,
        item_id: p.item_id,
        quantity: p.quantity,
        is_optional: p.is_optional,
      })) ?? [],
  };
}

/** Strip client ids and serialise choice options. */
export function toPayloadRows(
  tasks: TaskRow[],
  parts: PartRow[],
): { tasks: JobTaskIn[]; parts: JobPartIn[] } {
  return {
    tasks: tasks
      .filter((t) => t.title.trim())
      .map((t) => ({
        title: t.title.trim(),
        instructions: t.instructions,
        est_minutes: t.est_minutes || 15,
        is_mandatory: t.is_mandatory,
        checklist: t.checklist
          .filter((c) => c.prompt.trim())
          .map((c) => {
            const constraints: Record<string, unknown> = { ...(c.constraints ?? {}) };
            if (c.response_type === "choice") {
              constraints.options = (c._options ?? "")
                .split(",")
                .map((o) => o.trim())
                .filter(Boolean);
            } else {
              delete constraints.options;
            }
            return {
              prompt: c.prompt.trim(),
              response_type: c.response_type,
              constraints,
              is_mandatory: c.is_mandatory,
              photo_required: c.photo_required,
            };
          }),
      })),
    parts: parts
      .filter((p) => p.item_id)
      .map((p) => ({
        item_id: p.item_id,
        quantity: Number(p.quantity) || 1,
        is_optional: p.is_optional,
      })),
  };
}

const newTask = (): TaskRow => ({
  _id: localId(),
  title: "",
  instructions: "",
  est_minutes: 15,
  is_mandatory: true,
  checklist: [],
});
const newPart = (): PartRow => ({ _id: localId(), item_id: "", quantity: 1, is_optional: false });

/** Job template editor page (create when no id). */
export function JobTemplateEditorPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { templateId } = useParams({ strict: false });
  const existing = useJobTemplate(templateId);
  const assetTypes = useAssetTypesAdmin();
  const save = useSaveJobTemplate();
  const manage = can("maintenance.jobtemplate.manage");

  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [parts, setParts] = useState<PartRow[]>([]);
  const [typeIds, setTypeIds] = useState<string[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const linkedDocuments = useEntityDocuments(
    "jobtemplate",
    templateId ?? "",
    !!templateId && can("documents.document.view"),
  );

  const initial = useMemo<HeaderValues>(
    () => ({
      code: existing.data?.code ?? "",
      name: existing.data?.name ?? "",
      name_ar: existing.data?.name_i18n?.ar ?? "",
      category: existing.data?.category ?? "service",
      description: existing.data?.description ?? "",
      instructions: existing.data?.instructions ?? "",
      sop_document_id: existing.data?.sop_document_id ?? "",
      est_duration_minutes: String(existing.data?.est_duration_minutes ?? 60),
      est_labour_minutes: String(existing.data?.est_labour_minutes ?? 60),
      requires_verification: existing.data?.requires_verification ?? true,
      requires_meter_reading: existing.data?.requires_meter_reading ?? false,
      skills: existing.data?.skills.join(", ") ?? "",
      is_active: existing.data?.is_active ?? true,
    }),
    [existing.data],
  );
  const form = useForm<HeaderValues>({
    initial,
    schema: z.object({
      code: templateId ? z.string() : z.string().trim().min(1, t("app.required")),
      name: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "jt",
  });

  // Hydrate editable rows once the template loads (and again if a different one loads).
  useEffect(() => {
    if (existing.data && existing.data.id !== loadedId) {
      const rows = toRows(existing.data);
      setTasks(rows.tasks);
      setParts(rows.parts);
      setTypeIds(existing.data.applicable_asset_type_ids);
      form.reset(initial);
      setLoadedId(existing.data.id);
    }
  }, [existing.data, loadedId]);

  const updateTask = (id: string, patch: Partial<TaskRow>): void =>
    setTasks(tasks.map((x) => (x._id === id ? { ...x, ...patch } : x)));
  const updatePart = (id: string, patch: Partial<PartRow>): void =>
    setParts(parts.map((x) => (x._id === id ? { ...x, ...patch } : x)));

  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const rows = toPayloadRows(tasks, parts);
      const common = {
        name: v.name.trim(),
        name_i18n: (v.name_ar
          ? { en: v.name.trim(), ar: v.name_ar }
          : { en: v.name.trim() }) as Record<string, string>,
        category: v.category,
        description: v.description,
        instructions: v.instructions,
        sop_document_id: v.sop_document_id || null,
        est_duration_minutes: Number(v.est_duration_minutes) || 60,
        est_labour_minutes: Number(v.est_labour_minutes) || 60,
        requires_verification: v.requires_verification,
        requires_meter_reading: v.requires_meter_reading,
        skills: v.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        applicable_asset_type_ids: typeIds,
        tasks: rows.tasks,
        parts: rows.parts,
      };
      const body: JobTemplateIn | JobTemplatePatch = templateId
        ? { ...common, is_active: v.is_active }
        : { ...common, code: v.code.trim() };
      const saved = await save.mutateAsync({ id: templateId, body });
      if (!templateId) {
        await navigate({
          to: "/admin/job-templates/$templateId",
          params: { templateId: saved.id },
        });
      }
    });
    if (ok && templateId) {
      setLoadedId(null); // re-hydrate rows from the refreshed template
    }
  };

  if (templateId && existing.isPending) {
    return <LoadingState />;
  }
  if (templateId && existing.isError) {
    return <ErrorState error={existing.error} onRetry={() => void existing.refetch()} />;
  }

  const totalMinutes = tasks.reduce((sum, x) => sum + (x.est_minutes || 0), 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      noValidate
      className="@container"
    >
      <PageHeader
        title={templateId ? `${form.values.code} · ${form.values.name}` : t("jobTemplates.new")}
        subtitle={
          templateId
            ? t("jobTemplates.usedBy", { count: existing.data?.plan_count ?? 0 })
            : t("jobTemplates.subtitle")
        }
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate({ to: "/admin/job-templates" })}
            >
              {t("app.cancel")}
            </Button>
            {manage ? (
              <Button type="submit" disabled={form.submitting}>
                {t("app.save")}
              </Button>
            ) : null}
          </>
        }
      />
      {form.errors._ ? (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {form.errors._}
        </p>
      ) : null}
      {!form.dirty && save.isSuccess ? (
        <p role="status" className="mb-3 text-sm text-emerald-700">
          {t("app.saved")}
        </p>
      ) : null}
      <div className="grid gap-4 @5xl:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("jobTemplates.details")}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field id="jt-code" label={t("app.code")} error={form.errors.code}>
                <Input {...form.field("code")} disabled={!!templateId || !manage} />
              </Field>
              <Field id="jt-name" label={t("app.name")} error={form.errors.name}>
                <Input {...form.field("name")} disabled={!manage} />
              </Field>
              <Field id="jt-name_ar" label={t("assetTypes.nameAr")}>
                <Input dir="rtl" {...form.field("name_ar")} disabled={!manage} />
              </Field>
              <Field id="jt-category" label={t("assetTypes.category")}>
                <Select
                  id="jt-category"
                  value={form.values.category}
                  onChange={(e) => form.set("category", e.target.value)}
                  disabled={!manage}
                >
                  {TEMPLATE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`jobTemplates.categories.${c}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field id="jt-est_duration_minutes" label={t("jobTemplates.duration")}>
                <Input
                  type="number"
                  min="0"
                  {...form.field("est_duration_minutes")}
                  disabled={!manage}
                />
              </Field>
              <Field id="jt-est_labour_minutes" label={t("jobTemplates.labour")}>
                <Input
                  type="number"
                  min="0"
                  {...form.field("est_labour_minutes")}
                  disabled={!manage}
                />
              </Field>
              <Field id="jt-skills" label={t("members.skills")} hint={t("app.tagsHint")}>
                <Input {...form.field("skills")} disabled={!manage} />
              </Field>
              <div className="flex flex-wrap items-end gap-4 pb-2">
                <Checkbox
                  id="jt-requires_verification"
                  label={t("workOrders.requiresVerification")}
                  checked={form.values.requires_verification}
                  onChange={(e) => form.set("requires_verification", e.target.checked)}
                  disabled={!manage}
                />
                <Checkbox
                  id="jt-requires_meter_reading"
                  label={t("jobTemplates.requiresMeter")}
                  checked={form.values.requires_meter_reading}
                  onChange={(e) => form.set("requires_meter_reading", e.target.checked)}
                  disabled={!manage}
                />
                {templateId ? (
                  <Checkbox
                    id="jt-is_active"
                    label={t("app.active")}
                    checked={form.values.is_active}
                    onChange={(e) => form.set("is_active", e.target.checked)}
                    disabled={!manage}
                  />
                ) : null}
              </div>
              <Field id="jt-description" label={t("app.description")}>
                <Textarea {...form.field("description")} disabled={!manage} />
              </Field>
              <Field
                id="jt-instructions"
                label={t("jobTemplates.instructions")}
                hint={t("jobTemplates.instructionsHint")}
              >
                <Textarea {...form.field("instructions")} disabled={!manage} />
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>
                {t("workOrders.tasks")} ({tasks.length}) · {totalMinutes} min
              </CardTitle>
              {manage ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setTasks([...tasks, newTask()])}
                >
                  {t("jobTemplates.addTask")}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("jobTemplates.noTasks")}</p>
              ) : (
                <SortableList
                  items={tasks}
                  getId={(x) => x._id}
                  onChange={setTasks}
                  renderItem={(task, i) => (
                    <div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_6rem]">
                        <Input
                          aria-label={`${t("app.title")} ${i + 1}`}
                          placeholder={t("app.title")}
                          value={task.title}
                          onChange={(e) => updateTask(task._id, { title: e.target.value })}
                          disabled={!manage}
                        />
                        <Input
                          aria-label={`${t("jobTemplates.minutes")} ${i + 1}`}
                          type="number"
                          min="0"
                          value={task.est_minutes}
                          onChange={(e) =>
                            updateTask(task._id, { est_minutes: Number(e.target.value) })
                          }
                          disabled={!manage}
                        />
                        <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                          <Checkbox
                            id={`task-${task._id}-mand`}
                            label={t("app.required")}
                            checked={task.is_mandatory}
                            onChange={(e) =>
                              updateTask(task._id, { is_mandatory: e.target.checked })
                            }
                            disabled={!manage}
                          />
                          {manage ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              aria-label={`${t("app.delete")} ${i + 1}`}
                              onClick={() => setTasks(tasks.filter((x) => x._id !== task._id))}
                            >
                              ×
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      <Textarea
                        className="mt-2 min-h-10"
                        aria-label={`${t("jobTemplates.instructions")} ${i + 1}`}
                        placeholder={t("jobTemplates.instructions")}
                        value={task.instructions}
                        onChange={(e) => updateTask(task._id, { instructions: e.target.value })}
                        disabled={!manage}
                      />
                      <ChecklistEditor
                        items={task.checklist}
                        onChange={(checklist) => updateTask(task._id, { checklist })}
                        taskIndex={i}
                      />
                    </div>
                  )}
                />
              )}
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("jobTemplates.applicableTypes")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {t("jobTemplates.applicableTypesHint")}
              </p>
              {(assetTypes.data ?? [])
                .filter((x) => x.is_active)
                .map((x) => (
                  <div key={x.id}>
                    <Checkbox
                      id={`jt-type-${x.key}`}
                      label={x.name}
                      checked={typeIds.includes(x.id)}
                      onChange={(e) =>
                        setTypeIds(
                          e.target.checked
                            ? [...typeIds, x.id]
                            : typeIds.filter((id) => id !== x.id),
                        )
                      }
                      disabled={!manage}
                    />
                  </div>
                ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>{t("workOrders.parts")}</CardTitle>
              {manage ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setParts([...parts, newPart()])}
                >
                  {t("jobTemplates.addPart")}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-2">
              {parts.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("jobTemplates.noParts")}</p>
              ) : null}
              {parts.map((p, i) => (
                <div key={p._id} className="space-y-1 rounded-md border p-2">
                  <ItemPicker
                    id={`jt-part-${p._id}`}
                    value={p.item_id}
                    onChange={(id) => updatePart(p._id, { item_id: id ?? "" })}
                    disabled={!manage}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      aria-label={`${t("app.quantity")} ${i + 1}`}
                      type="number"
                      step="any"
                      min="0"
                      className="w-24"
                      value={p.quantity}
                      onChange={(e) => updatePart(p._id, { quantity: e.target.value })}
                      disabled={!manage}
                    />
                    <Checkbox
                      id={`jt-part-${p._id}-opt`}
                      label={t("app.optional")}
                      checked={p.is_optional}
                      onChange={(e) => updatePart(p._id, { is_optional: e.target.checked })}
                      disabled={!manage}
                    />
                    {manage ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`${t("app.delete")} ${t("workOrders.parts")} ${i + 1}`}
                        onClick={() => setParts(parts.filter((x) => x._id !== p._id))}
                      >
                        ×
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          {templateId ? (
            <Card>
              <CardHeader>
                <CardTitle>{t("jobTemplates.sopLinked")}</CardTitle>
              </CardHeader>
              <CardContent>
                <Attachments entityType="jobtemplate" entityId={templateId} camera={false} />
                <Field
                  label={t("jobTemplates.sopLinked")}
                  id="jt-sop"
                  hint={t("jobTemplates.sopSnapshotHint")}
                >
                  <Select
                    id="jt-sop"
                    value={form.values.sop_document_id}
                    onChange={(event) => form.set("sop_document_id", event.target.value)}
                    disabled={!manage || linkedDocuments.isError}
                  >
                    <option value="">{t("app.none")}</option>
                    {form.values.sop_document_id &&
                    !linkedDocuments.data?.some(
                      (document) => document.id === form.values.sop_document_id,
                    ) ? (
                      <option value={form.values.sop_document_id}>
                        {t("jobTemplates.sopUnavailable")}
                      </option>
                    ) : null}
                    {linkedDocuments.data?.map((document) => (
                      <option key={document.id} value={document.id}>
                        {document.code} · {document.title}
                      </option>
                    ))}
                  </Select>
                </Field>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </form>
  );
}
